import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Image, Trash2, Lock } from 'lucide-react';
import { MoneyInput } from '../MoneyInput';
import { Attachments } from '../Attachments';
import { fmtDate } from '../../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function ReceptionDetailDialog({ open, onOpenChange, reception, user, unitOf = () => 'unidad', canDeleteFC, canLoadComprobantes = true, onAddComp, onDeleteFC, onSavePrices, savingPrices = false }) {
  const pricesPending = !!reception?.prices_pending;
  const canValorize = pricesPending && canLoadComprobantes;
  const [priceDraft, setPriceDraft] = useState([]);
  useEffect(() => {
    if (reception) {
      setPriceDraft((reception.items || []).map(it => (it.cost_price > 0 ? String(it.cost_price) : '')));
    }
  }, [reception]);
  const items = reception?.items || [];
  const pricesValid = priceDraft.length > 0
    && items.some(it => (it.quantity_received || 0) > 0)
    && priceDraft.every((p, i) => (items[i]?.quantity_received || 0) <= 0 || parseFloat(p) > 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalle de Recepción</DialogTitle>
          <DialogDescription>{reception?.reception_number}</DialogDescription>
        </DialogHeader>

        {reception && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">OC</p>
                <p className="font-medium">{reception.order_number}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Depósito Destino</p>
                <p className="font-medium">{reception.warehouse_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Recibido por</p>
                <p className="font-medium">{reception.received_by_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fecha</p>
                <p className="font-medium">{fmtDate(reception.received_at)}</p>
              </div>
            </div>

            {reception.deviation_notes && (
              <div className="bg-amber-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-amber-800">Notas de desvío:</p>
                <p className="text-sm">{reception.deviation_notes}</p>
              </div>
            )}

            {reception.shortage_items?.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg" data-testid="shortage-banner">
                <p className="text-sm font-medium text-orange-800">Artículos con faltante (el proveedor no los envió completos):</p>
                <ul className="text-sm mt-1 space-y-0.5">
                  {reception.shortage_items.map((s, i) => (
                    <li key={i} data-testid={`shortage-item-${i}`}>• <b>{s.article_name}</b>: pedido {s.quantity_expected}, recibido {s.quantity_received} (faltaron {s.shortage})</li>
                  ))}
                </ul>
                <p className="text-xs text-orange-700 mt-1">La OC cerró como "Recibida c/Faltante". La reposición se gestiona con una nueva NP (los pedidos internos afectados vuelven a Demanda Insatisfecha).</p>
              </div>
            )}

            <div className="border rounded-md">
              {canValorize && (
                <div className="p-3 bg-orange-50 border-b text-xs text-orange-800 flex items-start gap-2" data-testid="valorize-banner">
                  <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                  <span><b>Valorización pendiente.</b> Cargá el <b>Precio Neto Unitario</b> de cada renglón según la factura del proveedor. Las <b>cantidades, lotes y vencimientos NO se pueden modificar</b>: son lo que verificó físicamente {reception.received_by_name || 'el receptor'} (control cruzado).</span>
                </div>
              )}
              {pricesPending && !canLoadComprobantes && (
                <div className="p-3 bg-slate-50 border-b text-xs text-muted-foreground" data-testid="prices-pending-note">
                  Precios pendientes de valorización por Administración.
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Artículo</TableHead>
                    <TableHead className="text-center">Autorizado (OC)</TableHead>
                    <TableHead className="text-center">Recibido</TableHead>
                    <TableHead className="text-center">Lote</TableHead>
                    <TableHead className="text-center">Vencimiento</TableHead>
                    <TableHead className="text-center">{canValorize ? 'Precio Neto Unit. ($) *' : 'Precio'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reception.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.article_name} <span className="text-xs text-muted-foreground">· {unitOf(item.article_id)}</span></TableCell>
                      <TableCell className="text-center text-muted-foreground" data-testid={`detail-authorized-${index}`}>
                        {item.quantity_expected > 0 ? item.quantity_expected : '—'}
                      </TableCell>
                      <TableCell className={`text-center ${item.quantity_expected > 0 && (item.quantity_received || 0) < item.quantity_expected ? 'text-orange-700 font-semibold' : ''}`}>
                        {item.quantity_received}
                      </TableCell>
                      <TableCell className="text-center font-mono text-xs">{item.lot_number || '-'}</TableCell>
                      <TableCell className="text-center text-xs">{item.expiration_date || '-'}</TableCell>
                      <TableCell className="text-center">
                        {canValorize ? (
                          (item.quantity_received || 0) > 0 ? (
                            <MoneyInput
                              value={priceDraft[index] ?? ''}
                              onChange={(e) => setPriceDraft(prev => prev.map((p, i) => i === index ? e.target.value : p))}
                              className="w-28 text-right mx-auto"
                              data-testid={`valorize-price-${index}`}
                            />
                          ) : (
                            <span className="text-xs text-muted-foreground" data-testid={`valorize-no-price-${index}`}>No ingresó</span>
                          )
                        ) : (
                          pricesPending ? <span className="text-muted-foreground">—</span> : `$${item.cost_price?.toFixed(2)}`
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {canValorize && (
                <div className="p-3 border-t flex items-center justify-between gap-3 bg-orange-50/50">
                  <span className="text-xs text-muted-foreground">
                    Total valorizado: <b>{fmt((reception.items || []).reduce((s, it, i) => s + (it.quantity_received || 0) * (parseFloat(priceDraft[i]) || 0), 0))}</b>
                  </span>
                  <Button
                    size="sm"
                    onClick={() => onSavePrices(reception, priceDraft)}
                    disabled={!pricesValid || savingPrices}
                    data-testid="save-prices-btn"
                  >
                    {savingPrices ? 'Guardando...' : 'Guardar valorización'}
                  </Button>
                </div>
              )}
            </div>

            {reception.prices_loaded_by_name && (
              <p className="text-xs text-muted-foreground" data-testid="valorized-by-note">
                Valorizado por <b>{reception.prices_loaded_by_name}</b> el {fmtDate(reception.prices_loaded_at)}
              </p>
            )}

            {canLoadComprobantes && pricesPending && (
              <div className="border rounded-md p-3 bg-slate-50 text-xs text-muted-foreground" data-testid="fc-blocked-note">
                La carga del <b>comprobante fiscal</b> se habilita después de guardar la valorización de los ítems.
              </div>
            )}

            {(reception.fiscal_invoices?.length > 0) ? (
              <div className="space-y-2" data-testid="reception-fiscal-invoices">
                <div className="flex justify-between items-center">
                  <p className="text-sm font-semibold">Comprobantes Fiscales ({reception.fiscal_invoices.length})</p>
                  {canLoadComprobantes && !pricesPending && (
                    <Button size="sm" variant="outline" onClick={() => onAddComp(reception)} data-testid="add-fc-to-reception-btn">
                      + Agregar otro comprobante
                    </Button>
                  )}
                </div>
                {reception.fiscal_invoices.map((fc, idx) => {
                  const compNum = `${fc.tipo_comprobante || ''} ${fc.letra || ''} ${fc.punto_venta || ''}-${fc.numero || ''}`.replace(/\s+/g, ' ').trim();
                  return (
                    <div key={fc.purchase_invoice_id || idx} className="border rounded-md p-3 bg-blue-50/40">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-blue-900">{compNum}</p>
                          <p className="text-xs text-muted-foreground">{(fc.supplier_snapshot || {}).business_name || '—'} · CUIT {(fc.supplier_snapshot || {}).tax_id || '—'}</p>
                        </div>
                        {fc.attachment_id && (
                          <a href={`${BACKEND_URL}/api/purchase-invoices/image/${fc.attachment_id}`} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                             data-testid={`reception-fc-link-${idx}`}>
                            <Image className="w-3 h-3" /> Ver adjunto
                          </a>
                        )}
                        {canDeleteFC && (
                          <button
                            type="button"
                            onClick={() => onDeleteFC({ fc, recId: reception.reception_id })}
                            className="ml-2 text-red-600 hover:text-red-800"
                            title="Eliminar comprobante (reversa cta. cte. y asiento)"
                            data-testid={`reception-fc-delete-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-5 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Fecha:</span> {fc.fecha_comprobante || '-'}</div>
                        <div><span className="text-muted-foreground">Neto:</span> {fmt(fc.neto_gravado_total)}</div>
                        <div><span className="text-muted-foreground">IVA:</span> {fmt(fc.iva_total)}</div>
                        <div><span className="text-muted-foreground">Percep.:</span> {fmt(fc.percepciones_total)}</div>
                        <div className="font-semibold text-right"><span className="text-muted-foreground">Total:</span> {fmt(fc.total)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (reception.invoice_type || reception.invoice_number || reception.invoice_date) ? (
              <div className="border rounded-md p-3 bg-slate-50 text-sm">
                <div className="flex justify-between items-center mb-2">
                  <p className="font-medium">Comprobante (resumen)</p>
                  {canLoadComprobantes && !pricesPending && (
                    <Button size="sm" variant="outline" onClick={() => onAddComp(reception)} data-testid="add-fc-to-reception-btn-2">
                      + Agregar comprobante
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><span className="text-muted-foreground">Tipo:</span> {reception.invoice_type || '-'}</div>
                  <div><span className="text-muted-foreground">Nro:</span> {reception.invoice_number || '-'}</div>
                  <div><span className="text-muted-foreground">Fecha:</span> {reception.invoice_date || '-'}</div>
                </div>
              </div>
            ) : (
              canLoadComprobantes && !pricesPending && (
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={() => onAddComp(reception)} data-testid="add-fc-to-reception-btn-3">
                    + Agregar comprobante fiscal
                  </Button>
                </div>
              )
            )}

            {reception.invoice_image && (
              <div>
                <p className="text-sm font-medium mb-2">Factura adjunta (legacy):</p>
                <img
                  src={reception.invoice_image}
                  alt="Factura"
                  className="max-w-full max-h-64 border rounded"
                />
              </div>
            )}

            <div className="border-t pt-4">
              <Attachments
                entityType="reception"
                entityId={reception.reception_id}
                currentUser={user}
                canUpload={!reception.final_status}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
